import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  Landmark,
  FileText,
  FolderTree,
  Hash,
  Layers,
  ListTree,
  Plus,
  Search
} from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailIdentity,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { SidebarForm } from '../../../../../components/shared/SidebarForm/SidebarForm';
import { FilterSelect } from '../../../../../components/shared/FilterSelect/FilterSelect';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { useToast } from '../../../../../hooks/useToast';
import {
  AccountLevel,
  ChartAccount,
  LEVEL_LABELS,
  PARENT_LEVEL,
  PGC_GROUPS,
  createChartOfAccount,
  listChartOfAccounts,
  validateNewAccount
} from '../../../../../services/ChartOfAccountsService';
import { BankAccountRecord, createBankAccountWithAccount, listBankAccounts } from '../../../../../services/BankAccountsService';
import '../../shared-modules.css';
import './FinanceMasters.css';

interface AccountForm {
  code: string;
  name: string;
  level: AccountLevel;
  parentId: string;
  // Datos bancarios: solo cuando la subcuenta es una 572
  iban: string;
  currency: string;
  balance: string;
  // Si el usuario ya eligió el nivel a mano, escribir el código no lo vuelve a cambiar
  levelTouched: boolean;
}

const EMPTY_FORM: AccountForm = { code: '', name: '', level: 'account', parentId: '', iban: '', currency: 'EUR', balance: '0', levelTouched: false };

// El nivel lo marca la longitud del código: 1 grupo, 2 subgrupo, 3 cuenta, 4 o más subcuenta
const levelForCode = (code: string): AccountLevel =>
  code.length <= 1 ? 'group' : code.length === 2 ? 'subgroup' : code.length === 3 ? 'account' : 'subaccount';

// Una subcuenta 572 es una cuenta de bancos: al crearla se crea también la cuenta bancaria enlazada.
// Cuenta como tal si el código empieza por 572 o si su cuenta padre es la 572.
const isBankSubaccount = (code: string, level: AccountLevel, parentCode = '') =>
  level === 'subaccount' && (code.trim().startsWith('572') || parentCode === '572');

const GROUP_OPTIONS = Object.keys(PGC_GROUPS).map(g => ({
  value: g,
  label: `${g} · ${PGC_GROUPS[g].toUpperCase()}`,
  icon: Layers
}));

const levelLabel = (level: ChartAccount['level']) => (level ? LEVEL_LABELS[level] : '—');

const byCode = (a: ChartAccount, b: ChartAccount) => a.code.localeCompare(b.code);

export const FinanceChartOfAccountsView = () => {
  const { notify } = useToast();

  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [banks, setBanks] = useState<BankAccountRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [isGroupFilterOpen, setIsGroupFilterOpen] = useState(false);

  const [selectedAccount, setSelectedAccount] = useState<ChartAccount | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState<AccountForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const list = await listChartOfAccounts();
      setAccounts(list.sort(byCode));
      setListError('');
    } catch (err) {
      console.error('Error fetching chart of accounts:', err);
      setAccounts([]);
      setListError(err instanceof Error ? err.message : 'No se pudo cargar el plan de cuentas.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = () => listBankAccounts().then(setBanks).catch(err => console.error('Error fetching bank accounts:', err));

  useEffect(() => {
    fetchAccounts();
    fetchBanks();
  }, []);

  const handleCreateNewClick = () => {
    setSelectedAccount(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleSelectAccount = (account: ChartAccount) => {
    setIsFormOpen(false);
    setSelectedAccount(account);
  };

  const closePanel = () => {
    setIsFormOpen(false);
    setSelectedAccount(null);
  };

  // Clic fuera cierra la ficha, el formulario no
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedAccount) && !isFormOpen);

  useClickOutside(
    (target) => Boolean(target.closest('.filter-select-wrapper')),
    () => setIsGroupFilterOpen(false),
    isGroupFilterOpen
  );

  // Subcuenta 572 + cuenta bancaria enlazada, en una sola llamada. La cuenta padre (572) se
  // enlaza si existe en el plan; no es obligatoria para poder dar de alta el banco.
  const handleSaveBankAccount = async (code: string, name: string) => {
    if (!/^572[0-9]{1,7}$/.test(code)) {
      setFormError('Una subcuenta de bancos empieza por 572 y tiene entre 4 y 10 dígitos (p. ej. 5720001).');
      return;
    }
    if (accounts.some(a => a.code === code)) {
      setFormError(`Ya existe una cuenta con el código ${code}.`);
      return;
    }
    if (!/^[A-Za-z0-9 ]{0,34}$/.test(formData.iban.trim())) {
      setFormError('El IBAN solo admite letras y números (máx. 34 caracteres).');
      return;
    }
    if (!/^[A-Z]{3}$/.test(formData.currency.trim().toUpperCase())) {
      setFormError('La moneda son 3 letras (EUR, USD…).');
      return;
    }
    const balance = Number(formData.balance.replace(',', '.'));
    if (formData.balance.trim() === '' || isNaN(balance)) {
      setFormError('El saldo inicial no es un número válido.');
      return;
    }

    setIsSaving(true);
    try {
      const bank = await createBankAccountWithAccount({
        code,
        name,
        iban: formData.iban.trim(),
        currency: formData.currency.trim().toUpperCase(),
        balance
      });
      await Promise.all([fetchAccounts(), fetchBanks()]);
      setIsFormOpen(false);
      notify('Éxito', `Subcuenta ${bank.accountCode || code} y cuenta bancaria "${bank.name}" creadas y enlazadas.`, 'info');
    } catch (err) {
      console.error('Error creating bank account:', err);
      setFormError(err instanceof Error ? err.message : 'Error de comunicación con el servidor.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAccount = async () => {
    const code = formData.code.trim();
    const name = formData.name.trim();
    if (!code || !name) {
      setFormError('Código y denominación son obligatorios.');
      return;
    }
    if (isBankSubaccount(code, formData.level, accounts.find(a => a.id === formData.parentId)?.code)) {
      await handleSaveBankAccount(code, name);
      return;
    }
    const check = validateNewAccount(code, formData.level, formData.parentId, accounts);
    if ('error' in check) {
      setFormError(check.error);
      return;
    }

    setIsSaving(true);
    try {
      const created = await createChartOfAccount({
        code,
        name,
        level: formData.level,
        parentId: check.parent ? check.parent.id : undefined
      });
      setAccounts(prev => [...prev, created].sort(byCode));
      setSelectedAccount(created);
      setIsFormOpen(false);
      notify('Éxito', `Cuenta contable ${created.code} — ${created.name} creada en el PGC.`, 'info');
    } catch (err) {
      console.error('Error creating account:', err);
      setFormError(err instanceof Error ? err.message : 'Error de comunicación con el servidor.');
    } finally {
      setIsSaving(false);
    }
  };

  const parentOf = (account: ChartAccount) =>
    account.parentId ? accounts.find(a => a.id === account.parentId) : undefined;

  const bankOf = (account: ChartAccount) => banks.find(b => b.accountId && b.accountId === account.id);

  const childrenOf = (account: ChartAccount) =>
    accounts.filter(a => a.parentId && a.parentId === account.id);

  const query = searchQuery.toLowerCase();
  const filteredAccounts = accounts.filter(a =>
    (groupFilter === 'all' || a.code.charAt(0) === groupFilter) &&
    (a.code.includes(query) || a.name.toLowerCase().includes(query))
  );

  // La cuenta padre tiene que ser del nivel inmediatamente superior
  const parentOptions = accounts.filter(a => a.id && a.level === PARENT_LEVEL[formData.level]);

  const columns: ColumnDef<ChartAccount>[] = [
    {
      id: 'code',
      header: 'CÓDIGO',
      render: (a) => <span className="fin-master__code">{a.code}</span>
    },
    {
      id: 'name',
      header: 'DENOMINACIÓN',
      render: (a) => <div className="fin-master__name">{a.name}</div>
    },
    {
      id: 'level',
      header: 'NIVEL',
      render: (a) => <span className="fin-master__chip">{levelLabel(a.level)}</span>
    },
    {
      id: 'group',
      header: 'GRUPO PGC',
      render: (a) => (
        <span className="fin-master__sub">
          {a.code.charAt(0)} · {PGC_GROUPS[a.code.charAt(0)] || '—'}
        </span>
      )
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR CÓDIGO (EJ. 430, 572, 700) O DENOMINACIÓN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <FilterSelect
          isOpen={isGroupFilterOpen}
          onToggle={setIsGroupFilterOpen}
          onSelect={setGroupFilter}
          value={groupFilter}
          options={GROUP_OPTIONS}
          icon={Layers}
          allLabel="TODOS_LOS_GRUPOS"
        />
        <button onClick={handleCreateNewClick} className="module-create-btn" data-keep-detail>
          <Plus size={14} /> Nueva_Cuenta
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedAccount || isFormOpen)}>
        {listError ? (
          <div className="module-panel">
            <div className="module-panel-empty">{listError}</div>
          </div>
        ) : (
          <DataTable
            data={filteredAccounts}
            columns={columns}
            title={<><BookOpen size={14} className="module-panel-title-icon" /> Plan_De_Cuentas_PGC</>}
            subtitle={loading ? undefined : `TOTAL: ${filteredAccounts.length}`}
            isLoading={loading}
            getRowId={(a) => a.id || a.code}
            onRowClick={handleSelectAccount}
            selectedRowId={selectedAccount ? selectedAccount.id || selectedAccount.code : null}
            emptyText="No hay cuentas contables en el plan."
            actionsHeader="ACCIONES"
            actionsColumn={(a) => (
              <RowActionsMenu actions={[viewAction(() => handleSelectAccount(a))]} />
            )}
          />
        )}

        {isFormOpen ? (
          <SidebarForm
            expandable
            title="Alta_De_Cuenta_Contable"
            onClose={closePanel}
            submitLabel={isSaving ? 'Guardando...' : 'Crear_Cuenta'}
            submitDisabled={isSaving}
            onSubmit={handleSaveAccount}
          >
            {formError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{formError}</span>
              </div>
            )}

            <DetailSection title="1. Datos_De_La_Cuenta" icon={FileText}>
              <div className="sidebar-form-grid-2">
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Código PGC *</label>
                  <input
                    type="text"
                    required
                    pattern="[0-9]+"
                    title="Solo dígitos (0-9)"
                    value={formData.code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      // El nivel se ajusta solo a la longitud del código (se puede cambiar después)
                      setFormData(prev => {
                        const level = val.trim() && !prev.levelTouched ? levelForCode(val.trim()) : prev.level;
                        return { ...prev, code: val, level, parentId: level === prev.level ? prev.parentId : '' };
                      });
                    }}
                    placeholder="Ej. 629 (cuenta), 6291 (subcuenta), 5720001 (banco)"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Nivel *</label>
                  <select
                    value={formData.level}
                    onChange={(e) => { const val = e.target.value as AccountLevel; setFormData(prev => ({ ...prev, level: val, parentId: '', levelTouched: true })); }}
                    className="sidebar-form-input"
                  >
                    {(Object.keys(LEVEL_LABELS) as AccountLevel[]).map(l => (
                      <option key={l} value={l}>{LEVEL_LABELS[l]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Denominación *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, name: val })); }}
                  placeholder="Ej. Gastos de mensajería"
                  className="sidebar-form-input"
                />
              </div>
              {formData.level !== 'group' && (
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">Cuenta Padre (se deduce del código si se deja vacía)</label>
                  <select
                    value={formData.parentId}
                    onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, parentId: val })); }}
                    className="sidebar-form-input"
                  >
                    <option value="">— Deducir del código —</option>
                    {parentOptions.map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </DetailSection>

            {isBankSubaccount(formData.code, formData.level, accounts.find(a => a.id === formData.parentId)?.code) && (
              <DetailSection title="2. Datos_Bancarios" icon={Landmark}>
                <p className="fin-master__sub">
                  Es una subcuenta de bancos (572): se crea también la cuenta bancaria y queda enlazada con ella.
                  El nombre del banco es la denominación de la subcuenta. El código lleva 572 y de 1 a 7 dígitos más (p. ej. 5720001).
                </p>
                <div className="sidebar-form-group">
                  <label className="sidebar-form-label">IBAN</label>
                  <input
                    type="text"
                    value={formData.iban}
                    maxLength={34}
                    onChange={(e) => { const val = e.target.value.toUpperCase(); setFormData(prev => ({ ...prev, iban: val })); }}
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    className="sidebar-form-input"
                  />
                </div>
                <div className="sidebar-form-grid-2">
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Moneda *</label>
                    <input
                      type="text"
                      value={formData.currency}
                      maxLength={3}
                      onChange={(e) => { const val = e.target.value.toUpperCase(); setFormData(prev => ({ ...prev, currency: val })); }}
                      className="sidebar-form-input"
                    />
                  </div>
                  <div className="sidebar-form-group">
                    <label className="sidebar-form-label">Saldo inicial *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.balance}
                      onChange={(e) => { const val = e.target.value; setFormData(prev => ({ ...prev, balance: val })); }}
                      className="sidebar-form-input right"
                    />
                  </div>
                </div>
              </DetailSection>
            )}
          </SidebarForm>
        ) : selectedAccount ? (
          <DetailPanel
            expandable
            title="Ficha_De_Cuenta_Contable"
            onClose={closePanel}
            cards={
              <>
              {bankOf(selectedAccount) && (
                <DetailCard icon={Landmark} title="Cuenta_Bancaria_Enlazada">
                  <div className="fin-master__attachments">
                    <div className="fin-master__attachment">
                      <span>{bankOf(selectedAccount)!.name}</span>
                      <span className="fin-master__sub">{bankOf(selectedAccount)!.status === 'active' ? 'ACTIVA' : 'INACTIVA'}</span>
                    </div>
                    <div className="fin-master__attachment">
                      <span>IBAN</span>
                      <span className="fin-master__sub">{bankOf(selectedAccount)!.iban || '—'}</span>
                    </div>
                    <div className="fin-master__attachment">
                      <span>Saldo</span>
                      <span className="fin-master__sub">{bankOf(selectedAccount)!.balance.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {bankOf(selectedAccount)!.currency}</span>
                    </div>
                  </div>
                </DetailCard>
              )}
              <DetailCard icon={ListTree} title={`Cuentas_Dependientes (${childrenOf(selectedAccount).length})`}>
                {childrenOf(selectedAccount).length === 0 ? (
                  <div className="fin-master__empty">Sin cuentas dependientes</div>
                ) : (
                  <div className="fin-master__attachments">
                    {childrenOf(selectedAccount).map(child => (
                      <div key={child.id || child.code} className="fin-master__attachment">
                        <span>{child.code} - {child.name}</span>
                        <span className="fin-master__sub">{levelLabel(child.level)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </DetailCard>
              </>
            }
          >
            <DetailIdentity
              icon={BookOpen}
              reference={`PGC ${selectedAccount.code}`}
              name={selectedAccount.name}
              subtitle={<span className="fin-master__chip">{levelLabel(selectedAccount.level)}</span>}
            />

            <DetailFieldList>
              <DetailField icon={Hash} label="CÓDIGO">{selectedAccount.code}</DetailField>
              <DetailField icon={Layers} label="GRUPO_PGC">
                {`${selectedAccount.code.charAt(0)} · ${PGC_GROUPS[selectedAccount.code.charAt(0)] || '—'}`}
              </DetailField>
              <DetailField icon={FolderTree} label="CUENTA_PADRE">
                {parentOf(selectedAccount)
                  ? `${parentOf(selectedAccount).code} - ${parentOf(selectedAccount).name}`
                  : 'Ninguna (cuenta raíz)'}
              </DetailField>
            </DetailFieldList>
          </DetailPanel>
        ) : null}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceChartOfAccountsView;
